import AmpersandModel from "ampersand-model";
import ajaxMixin from "./ajax";
import dataTypes from "./data-types";
import isObject from "lodash/isObject";
import result from "lodash/result";
import { toDataTest } from "helpers/model";

const Model = AmpersandModel.extend(ajaxMixin, {
    idAttribute: "_id",
    forceModuleName: false,
    props: {
        _id: "objectId",
        __created_on: { type: "utcDate" },
        __modified_on: { type: "utcDate" },
    },
    dataTypes,
    get: function (attr) {
        const objectRegExp = new RegExp(/\./);
        const isNestedObject = objectRegExp.test(attr);
        let target = this;

        if (isNestedObject) {
            const fields = attr.split(".");
            while (fields && fields.length && target) {
                target = target[fields[0]];
                fields.shift();
            }

            return target;
        } else {
            return target[attr];
        }
    },

    toDataTest: function (attr) {
        return toDataTest(this.get(attr));
    },

    // Hack for now, prevent errors from happening in order
    set: function (key, value, options) {
        const self = this;
        const extraProperties = this.extraProperties;
        let newType, newVal, def, cast, err, attr, attrs, dataType, currentVal, hasChanged, isEqual, onChange;

        // Handle both `"key", value` and `{key: value}` -style arguments.
        if (isObject(key) || key === null) {
            attrs = key;
            options = value;
        } else {
            attrs = {};
            attrs[key] = value;
        }

        options = options || {};

        if (!this._validate(attrs, options)) {
            return false;
        }

        // Extract attributes and options.
        const unset = options.unset;
        const silent = options.silent;
        const initial = options.initial;

        // Initialize change tracking.
        const wasChanging = this._changing;
        this._changing = true;
        const changeEvents = [];

        // if not already changing, store previous
        if (initial) {
            this._previousAttributes = {};
        } else if (!wasChanging) {
            this._previousAttributes = this.attributes;
            this._changed = {};
        }

        // For each `set` attribute...
        for (let i = 0, keys = Object.keys(attrs || {}), len = keys.length; i < len; i++) {
            attr = keys[i];
            newVal = attrs[attr];
            newType = typeof newVal;
            currentVal = this._values[attr];
            def = this._definition[attr];

            if (!def) {
                // if this is a child model or collection
                if (this._children[attr] || this._collections[attr]) {
                    if (!isObject(newVal)) {
                        newVal = {};
                    }

                    this[attr].set(newVal, options);
                    continue;
                } else if (extraProperties === "ignore") {
                    continue;
                } else if (extraProperties === "reject") {
                    throw new TypeError(
                        'No "' +
                            attr +
                            '" property defined on ' +
                            (this.type || "this") +
                            ' model and extraProperties not set to "ignore" or "allow"',
                    );
                } else if (extraProperties === "allow") {
                    def = this._createPropertyDefinition(attr, "any");
                } else if (extraProperties) {
                    throw new TypeError('Invalid value for extraProperties: "' + extraProperties + '"');
                }
            }

            isEqual = this._getCompareForType(def.type);
            onChange = this._getOnChangeForType(def.type);
            dataType = this._dataTypes[def.type];

            // check type if we have one
            if (dataType && dataType.set) {
                cast = dataType.set(newVal);
                newVal = cast.val;
                newType = cast.type;
            }

            // If we've defined a test, run it
            if (def.test) {
                err = def.test.call(this, newVal, newType);
                if (err) {
                    throw new TypeError("Property '" + attr + "' failed validation with error: " + err);
                }
            }

            // If we are required but undefined, throw error.
            // If we are null and are not allowing null, throw error
            // If we have a defined type and the new type doesn't match, and we are not null, throw error.
            // If we require specific value and new one is not one of them, throw error (unless it has default value or we're unsetting it with undefined).

            if (newVal === undefined && def.required) {
                throw new TypeError(
                    "Required property '" + attr + "' must be of type " + def.type + ". Tried to set " + newVal,
                );
            }
            if (newVal === null && def.required && !def.allowNull) {
                throw new TypeError(
                    "Property '" + attr + "' must be of type " + def.type + " (cannot be null). Tried to set " + newVal,
                );
            }
            if (def.type && def.type !== "any" && def.type !== newType && newVal !== null && newVal !== undefined) {
                // add compatibility for order schema fields that changed from multi to single value type
                if (def.type === "string" && Array.isArray(newVal)) {
                    newType = "string";
                    newVal = newVal[0];
                } else if (def.type === "array" && !Array.isArray(newVal)) {
                    newType = "array";
                    newVal = [newVal];
                } else {
                    throw new TypeError(
                        "Property '" + attr + "' must be of type " + def.type + ". Tried to set " + newVal,
                    );
                }
            }
            if (def.values && !def.values.includes(newVal)) {
                const defaultValue = result(def, "default");
                if (unset && defaultValue !== undefined) {
                    newVal = defaultValue;
                }
                // CHANGED FROM AMPERSAND: Removed this check
                /*
                else if (!unset || (unset && newVal !== undefined)) {
                    throw new TypeError('Property \'' + attr + '\' must be one of values: ' + def.values.join(', ') + '. Tried to set ' + newVal);
                }
                */
            }

            // We know this has 'changed' if it's the initial set, so skip a potentially expensive isEqual check.
            hasChanged = initial || !isEqual(currentVal, newVal, attr);

            // enforce `setOnce` for properties if set
            if (def.setOnce && currentVal !== undefined && hasChanged) {
                throw new TypeError("Property '" + attr + "' can only be set once.");
            }

            // set/unset attributes.
            // If this is not the initial set, keep track of changed attributes
            // and push to changeEvents array so we can fire events.
            if (hasChanged) {
                // This fires no matter what, even on initial set.
                onChange(newVal, currentVal, attr);

                // If this is a change (not an initial set), mark the change.
                // Note it's impossible to unset on the initial set (it will already be unset),
                // so we only include that logic here.
                if (!initial) {
                    this._changed[attr] = newVal;
                    this._previousAttributes[attr] = currentVal;
                    if (unset) {
                        // FIXME delete is very slow. Can we get away with setting to undefined?
                        delete this._values[attr];
                    }
                    if (!silent) {
                        changeEvents.push({ prev: currentVal, val: newVal, key: attr });
                    }
                }
                if (!unset) {
                    this._values[attr] = newVal;
                }
            } else {
                // Not changed
                // FIXME delete is very slow. Can we get away with setting to undefined?
                delete this._changed[attr];
            }
        }

        // Fire events. This array is not populated if we are told to be silent.
        if (changeEvents.length) {
            this._pending = true;
        }
        changeEvents.forEach(function (change) {
            self.trigger("change:" + change.key, self, change.val, options);
        });

        // You might be wondering why there's a `while` loop here. Changes can
        // be recursively nested within `"change"` events.
        if (wasChanging) {
            return this;
        }
        while (this._pending) {
            this._pending = false;
            this.trigger("change", this, options);
        }
        this._pending = false;
        this._changing = false;
        return this;
    },

    // For speed but date doesnt get parsed properly
    /*
    set (key, value, options = {}) {
        let attrs;
        if (isObject(key) || key === null) {
            attrs = key;
            options = value || {};
        } else {
            attrs = {};
            attrs[key] = value;
        }

        if(options.xhr && this.isNew()) {
            this.fastSet(attrs, options);
            return this;
        }
        return AmpersandModel.prototype.set.call(this, attrs, options);
    },

    fastSet (attrs, options) {
        [this._children, this._collections].forEach((siblings) => {
            Object.keys(siblings).forEach((sibling) => {
                if(attrs[sibling] !== undefined) {
                    this[sibling].set(attrs[sibling], options);
                    delete attrs[sibling];
                }
            });
        });

        // only iterate of utcDate and other specialty setters that manipulate from native javascript objects
        for (let i = 0, keys = Object.keys(attrs), len = keys.length; i < len; i = i + 1) {
            const attr = keys[i];
            const def = this._definition[attr];
            if(def) {
                const dataType = this._dataTypes[def.type];
                if (dataType && dataType.set && !dataType.fastSet) {
                    attrs[attr] = dataType.set(attrs[attr]).val;
                }
            }
        }
        this._values = attrs;
    }
    */
});

export default Model;
